// Shipping price calculation — single source of truth shared by the web app
// (public/customer calculator + admin calculator page) and mobile-staff.
//
// The rate table itself is NOT here: it lives in backend/config/shippingRates.json
// and is served by GET /api/public/shipping-rates, so every client prices from the
// same live config. This file only holds the tier-matching rules.
//
// Plain JS (not TS) on purpose: mobile-staff imports it directly, while the web app
// copies it into frontend/src/generated/ via `npm run copy:shared` (CRA cannot import
// from outside src/). Same arrangement as shared/pageSlugs.json.

/** Parse a user-entered number; anything invalid/negative counts as 0. */
export const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/**
 * Pricing logic:
 *  - tier = first row where size >= L+W+H
 *  - weight > tier.weight     → tier.price + (weight - tier.weight) * overweightPerKg
 *  - weight >= 50% of tier    → tier.price
 *  - 30% <= weight < 50%      → previous tier's price (1 step down)
 *  - weight < 30%             → 2 tiers down
 *
 * @param {{tiers: Array<{tier:number,price:number,weight:number,size:number}>, overweightPerKg?: number}|null} rates
 * @param {{l: string|number, w: string|number, h: string|number}} dims
 * @param {string|number} weight
 * @returns {null | {oversize: true, totalSize: number, maxSize: number} | {
 *   totalSize: number, weight: number, tier: object, appliedTier: object,
 *   basePrice: number, extraKg?: number, extra?: number, total: number,
 *   rule: "overweight"|"normal"|"discount1"|"discount2"
 * }}
 */
export const calculateShipping = (rates, dims, weight) => {
  if (!rates || !rates.tiers?.length) return null;
  const totalSize = num(dims.l) + num(dims.w) + num(dims.h);
  const w = num(weight);
  if (totalSize <= 0 || w <= 0) return null;

  const tiers = rates.tiers;
  const maxSize = tiers[tiers.length - 1].size;
  if (totalSize > maxSize) {
    return { oversize: true, totalSize, maxSize };
  }

  const idx = tiers.findIndex((t) => totalSize <= t.size);
  const tier = tiers[idx];
  const halfW = tier.weight * 0.5;
  const thirtyW = tier.weight * 0.3;
  const perKg = rates.overweightPerKg ?? 10;

  if (w > tier.weight) {
    const extraKg = w - tier.weight;
    const extra = extraKg * perKg;
    return {
      totalSize, weight: w, tier, appliedTier: tier,
      basePrice: tier.price, extraKg, extra,
      total: tier.price + extra, rule: "overweight",
    };
  }
  if (w >= halfW) {
    return {
      totalSize, weight: w, tier, appliedTier: tier,
      basePrice: tier.price, total: tier.price, rule: "normal",
    };
  }
  if (w >= thirtyW) {
    const applied = tiers[Math.max(0, idx - 1)];
    return {
      totalSize, weight: w, tier, appliedTier: applied,
      basePrice: applied.price, total: applied.price, rule: "discount1",
    };
  }
  const applied = tiers[Math.max(0, idx - 2)];
  return {
    totalSize, weight: w, tier, appliedTier: applied,
    basePrice: applied.price, total: applied.price, rule: "discount2",
  };
};
